import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, Download } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { PageHeader, Card, EmptyState, Loading, Field, Pager } from "../../components/common";
import { api, errMsg, newKey, downloadCsv } from "../../lib/api";
import { pkr, fmtDate, PAY_METHODS } from "../../lib/format";

function PayDialog({ group, onClose, onDone }) {
  const [sel, setSel] = useState(group.commissions.map((c) => c.id));
  const [f, setF] = useState({ method: group.executive.payment_details?.method || "bank_transfer", reference: "", paid_at: new Date().toISOString().slice(0, 10), notes: "" });
  const [key] = useState(newKey());
  const [busy, setBusy] = useState(false);
  const total = group.commissions.filter((c) => sel.includes(c.id)).reduce((s, c) => s + c.amount, 0);
  const [amount, setAmount] = useState(total);
  useEffect(() => setAmount(total), [total]);
  const pd = group.executive.payment_details || {};
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/commission-payments", { ...f, executive_id: group.executive.id, commission_ids: sel, amount: parseInt(amount), idempotency_key: key });
      toast.success("Payment recorded"); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Record payment · <span className="font-mono">{group.executive.sales_id}</span></DialogTitle></DialogHeader>
        <div className="rounded-lg bg-slate-50 p-3 text-sm"><p className="font-semibold">{group.executive.name}</p><p>{PAY_METHODS[pd.method] || "No method on file"} · {pd.bank_name} · {pd.account_title} <span className="font-mono">{pd.account_number}</span></p></div>
        <div className="max-h-44 space-y-2 overflow-y-auto">
          {group.commissions.map((c) => (
            <label key={c.id} className="flex items-center justify-between gap-3 rounded-lg border p-2 text-sm">
              <span className="flex items-center gap-2"><Checkbox checked={sel.includes(c.id)} onCheckedChange={(v) => setSel(v ? [...sel, c.id] : sel.filter((x) => x !== c.id))} data-testid={`pay-select-${c.order_no}`} /><span className="font-mono">{c.order_no}</span></span>
              <span className="font-mono font-semibold">{pkr(c.amount)}</span>
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Payment method"><Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}><SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PAY_METHODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></Field>
          <Field label="Payment date"><Input type="date" data-testid="pay-date" value={f.paid_at} onChange={(e) => setF({ ...f, paid_at: e.target.value })} /></Field>
        </div>
        <Field label="Amount paid (PKR)" hint={`Must equal selected total ${pkr(total)}`}><Input type="number" data-testid="pay-amount" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        <Field label="Transaction reference" required><Input data-testid="pay-reference" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} /></Field>
        <Field label="Notes"><Textarea rows={2} data-testid="pay-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <DialogFooter><Button data-testid="pay-submit" disabled={busy || !sel.length || f.reference.trim().length < 2} onClick={submit}>{busy ? "Saving…" : `Record ${pkr(total)} payment`}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentsLog() {
  const [f, setF] = useState({ sales_id: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => { const t = setTimeout(() => api.get("/admin/commission-payments", { params: { ...f, page } }).then((r) => setData(r.data)), 250); return () => clearTimeout(t); }, [f, page]);
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-heading text-xl font-semibold">Payment records</h2>
        <Button variant="outline" size="sm" onClick={() => downloadCsv("payments")} data-testid="payments-export-csv"><Download className="mr-2 h-4 w-4" />Export CSV</Button>
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        <Input placeholder="Sales ID" value={f.sales_id} onChange={(e) => setF({ ...f, sales_id: e.target.value })} className="h-10 w-44" data-testid="payments-filter-sales-id" />
        <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-10 w-44" />
        <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-10 w-44" />
        {data && <span className="self-center text-sm text-slate-600">Total in range: <b className="font-mono">{pkr(data.amount)}</b></span>}
      </div>
      {!data ? <Loading /> : data.items.length === 0 ? <EmptyState icon={Banknote} title="No payments recorded" /> : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Sales ID</TableHead><TableHead>Executive</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Method</TableHead><TableHead>Reference</TableHead><TableHead>Orders</TableHead></TableRow></TableHeader>
            <TableBody>{data.items.map((p) => (
              <TableRow key={p.id} data-testid={`payment-row-${p.reference}`}>
                <TableCell className="whitespace-nowrap">{fmtDate(p.paid_at)}</TableCell><TableCell className="whitespace-nowrap font-mono">{p.sales_id}</TableCell><TableCell>{p.executive_name}</TableCell>
                <TableCell className="text-right font-mono font-bold">{pkr(p.amount)}</TableCell><TableCell>{PAY_METHODS[p.method]}</TableCell><TableCell className="font-mono text-sm">{p.reference}</TableCell>
                <TableCell className="max-w-[220px] font-mono text-xs">{p.order_nos.join(", ")}</TableCell>
              </TableRow>))}</TableBody>
          </Table>
          <div className="px-4 pb-4"><Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} /></div>
        </Card>
      )}
    </div>
  );
}

export default function CommissionPayments() {
  const [groups, setGroups] = useState(null);
  const [pay, setPay] = useState(null);
  const [v, setV] = useState(0);
  const load = () => api.get("/admin/payables").then((r) => setGroups(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-10">
      <div>
        <PageHeader title="Commission Payments" subtitle="Pay approved commissions and record the transaction reference." />
        {!groups ? <Loading /> : groups.length === 0 ? <EmptyState icon={Banknote} title="No approved commissions awaiting payment" /> : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {groups.map((g) => (
              <Card key={g.executive.id} className="p-5" data-testid={`payable-${g.executive.sales_id}`}>
                <p className="font-mono text-sm font-bold">{g.executive.sales_id}</p>
                <p className="text-slate-700">{g.executive.name}</p>
                <p className="mt-3 font-mono text-2xl font-bold text-emerald-700">{pkr(g.total)}</p>
                <p className="text-xs text-slate-500">{g.count} approved commission{g.count > 1 ? "s" : ""}</p>
                <Button className="mt-4 h-11 w-full" onClick={() => setPay(g)} data-testid={`record-payment-${g.executive.sales_id}`}>Record payment</Button>
              </Card>
            ))}
          </div>
        )}
      </div>
      <PaymentsLog key={v} />
      {pay && <PayDialog group={pay} onClose={() => setPay(null)} onDone={() => { setPay(null); load(); setV(v + 1); }} />}
    </div>
  );
}
